import CloseIcon from "@mui/icons-material/Close";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { tokens } from "../../../theme";
import type { IBrandModel, IEquipment } from "../../../types/equipment";
import ContactSearchField from "./ContactSearch";

interface IContact {
  id: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

interface RentOutGearDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RentOutFormData) => Promise<void>;
  brandModel?: IBrandModel | null;
  equipment: IEquipment | null;
}

export interface RentOutFormData {
  clientId: string;
  clientName: string;
  startDate: string;
  endDate: string;
  dailyRate: string;
}

const initialForm: RentOutFormData = {
  clientId: "",
  clientName: "",
  startDate: "",
  endDate: "",
  dailyRate: "",
};

function calcDuration(start: string, end: string): string {
  if (!start || !end) return "--";
  const s = new Date(start);
  const e = new Date(end);
  const diff = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return "--";
  return `${diff} Days`;
}

export default function RentOutGearDialog({
  open,
  onClose,
  onSubmit,
  brandModel,
  equipment,
}: RentOutGearDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [form, setForm] = useState<RentOutFormData>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const rate = equipment?.rental_value || brandModel?.rental_value || "";
    setForm({ ...initialForm, dailyRate: rate?.toString() || "" });
  }, [open, equipment, brandModel]);

  const handleClose = () => {
    setForm(initialForm);
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    await onSubmit(form);
    setIsSubmitting(false);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "30px",
      backgroundColor: colors.primary[800],
      fontSize: 13,
      overflow: "hidden",
      paddingRight: "10px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: `1px solid ${colors.primary[400]}`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 20px",
      "&::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 0.8,
      },
    },
  };

  const compactDateFieldSx = {
    ...fieldSx,
    "& .MuiOutlinedInput-input": {
      padding: "12px 6px",
      fontSize: 13,
      minWidth: 0,
    },
    "& .MuiOutlinedInput-root": {
      ...fieldSx["& .MuiOutlinedInput-root"],
      minWidth: 0,
      paddingRight: "4px",
    },
  };

  const duration = calcDuration(form.startDate, form.endDate);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: colors.primary[900],
          boxShadow: "0px 10px 40px rgba(0,0,0,0.08)",
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: "absolute",
          right: 20,
          top: 20,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "50%",
          width: 26,
          height: 26,
          color: theme.palette.text.primary,
          zIndex: 10,
        }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>

      <DialogContent sx={{ p: "24px", overflowX: "hidden" }}>
        <Typography
          variant="h5"
          fontWeight={600}
          color={colors.primary[400]}
          mb={3}
        >
          Rent out Gear
        </Typography>

        {equipment && (
          <Box
            sx={{
              borderRadius: "16px",
              border: `1px solid ${theme.palette.divider}`,
              p: "16px 20px",
              mb: 3,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={0.5}
            >
              <Typography
                fontWeight={500}
                fontSize={16}
                color={theme.palette.text.primary}
              >
                {equipment.gear_name}
              </Typography>
              <Box
                sx={{
                  fontSize: 11,
                  color: "#1D9E75",
                  background: "#E1F5EE",
                  px: "10px",
                  py: "4px",
                  borderRadius: "12px",
                  fontWeight: 600,
                }}
              >
                {equipment.status || "Available"}
              </Box>
            </Box>
            <Typography
              fontSize={13}
              color={theme.palette.text.secondary}
              mb={2}
            >
              {equipment.brand_model?.brand_name}
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  background: colors.primary[800],
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "20px",
                }}
              >
                <LocationOnOutlinedIcon
                  sx={{ fontSize: 14, color: theme.palette.text.secondary }}
                />
                <Typography fontSize={11} color={theme.palette.text.secondary}>
                  {equipment.location || "Gourmet Garage, Harare"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <InventoryOutlinedIcon
                  sx={{ fontSize: 14, color: theme.palette.text.secondary }}
                />
                <Typography fontSize={11} color={theme.palette.text.secondary}>
                  {brandModel?.available_quantity} In stock
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <LabelOutlinedIcon
                  sx={{ fontSize: 14, color: theme.palette.text.secondary }}
                />
                <Typography fontSize={11} color={theme.palette.text.secondary}>
                  {equipment.serial_number || "SN-2023-001"}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Client Search */}
        <Typography
          variant="body2"
          color={theme.palette.text.secondary}
          display="block"
          mb={1}
        >
          Client <span style={{ color: "#E24B4A" }}>*</span>
        </Typography>
        <ContactSearchField
          onSelect={(contact: IContact | null) => {
            setForm((p) => ({
              ...p,
              clientId: contact ? String(contact.id) : "",
              clientName: contact
                ? `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
                : "",
            }));
          }}
        />

        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr 72px"
          gap={1}
          mb={2.5}
          sx={{ minWidth: 0, width: "100%", overflow: "hidden" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              sx={{ minWidth: 0, mb: 1 }}
            >
              Start Date <span style={{ color: "#E24B4A" }}>*</span>
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={form.startDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, startDate: e.target.value }))
              }
              sx={compactDateFieldSx}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              sx={{ minWidth: 0, mb: 1 }}
            >
              End Date <span style={{ color: "#E24B4A" }}>*</span>
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={form.endDate}
              onChange={(e) =>
                setForm((p) => ({ ...p, endDate: e.target.value }))
              }
              sx={compactDateFieldSx}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              mb={1}
            >
              Duration
            </Typography>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "30px",
                background: "transparent",
                height: "43px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography fontSize={13} color={theme.palette.text.secondary}>
                {duration}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box display="grid" gridTemplateColumns="1fr" gap={1} mb={4}>
          <Box>
            <Typography
              variant="body2"
              color={theme.palette.text.secondary}
              display="block"
              mb={1}
            >
              Daily Rate
            </Typography>
            <TextField
              fullWidth
              value={form.dailyRate ? `$${form.dailyRate}` : ""}
              onChange={(e) => {
                const raw = e.target.value.replace(/^\$/, "");
                setForm((p) => ({ ...p, dailyRate: raw }));
              }}
              sx={fieldSx}
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="flex-end">
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting || !form.clientId || !form.startDate || !form.endDate
            }
            style={{
              background: colors.primary[400],
              color: colors.primary[900],
              border: "none",
              borderRadius: "12px",
              padding: "14px 28px",
              fontSize: 15,
              fontWeight: 500,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity:
                isSubmitting ||
                !form.clientId ||
                !form.startDate ||
                !form.endDate
                  ? 0.6
                  : 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              transition: "opacity 0.2s ease",
            }}
          >
            {isSubmitting && <CircularProgress size={16} color="inherit" />}
            {isSubmitting ? "Confirming..." : "Confirm Rental"}
          </button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
